import React from "react";
import { Button } from "../components/Button";

export function Introduction({ next }) {
  return (
    <div className="mt-3 sm:mt-5 p-20">
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 500,
          padding: "4px 12px",
          borderRadius: 8,
          background: "var(--color-background-info)",
          color: "var(--color-text-info)",
          marginBottom: "1rem",
        }}
      >
        Instructions
      </div>

      <h3 className="text-2xl font-medium text-gray-900 mb-2">
        The Prisoner's Dilemma
      </h3>

      <p className="text-sm text-gray-500 mb-6 leading-relaxed">
        You and another participant will make a series of decisions. Your
        earnings depend on <em>both</em> your choice and your partner's choice —
        so think carefully.
      </p>

      {/* The Setup */}
      <div className="border border-gray-200 rounded-xl p-5 mb-4">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
          The setup
        </p>
        {[
          {
            n: "1",
            text: (
              <span>
                Each round, you and your partner simultaneously choose to either{" "}
                <strong>Cooperate</strong> or <strong>Defect</strong> — without
                seeing each other's choice first.
              </span>
            ),
          },
          {
            n: "2",
            text: "Once both decisions are made, both choices are revealed and your payoffs are calculated.",
          },
          {
            n: "3",
            text: (
              <span>
                This repeats for several rounds. Your{" "}
                <strong>total earnings</strong> across all rounds determine your
                final payment.
              </span>
            ),
          },
        ].map(({ n, text }) => (
          <div
            key={n}
            className="flex gap-3 items-start py-2 border-b border-gray-100 last:border-0"
          >
            <div className="w-6 h-6 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-medium text-gray-500 shrink-0">
              {n}
            </div>
            <p className="text-sm text-gray-700 leading-relaxed pt-0.5">
              {text}
            </p>
          </div>
        ))}
      </div>

      {/* Outcomes */}
      <div className="border border-gray-200 rounded-xl p-5 mb-4">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
          Possible outcomes each round
        </p>
        <div className="grid grid-cols-2 gap-2.5">
          <div className="rounded-lg p-3 bg-green-50 border border-green-200">
            <p className="text-xs font-medium text-green-800 mb-1">
              Both cooperate
            </p>
            <p className="text-sm font-medium text-gray-800">
              You: +3 pts · Them: +3 pts
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Mutual benefit</p>
          </div>
          <div className="rounded-lg p-3 bg-red-50 border border-red-200">
            <p className="text-xs font-medium text-red-800 mb-1">Both defect</p>
            <p className="text-sm font-medium text-gray-800">
              You: +1 pt · Them: +1 pt
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Mutual loss</p>
          </div>
          <div className="rounded-lg p-3 bg-amber-50 border border-amber-200">
            <p className="text-xs font-medium text-amber-800 mb-1">
              You defect, they cooperate
            </p>
            <p className="text-sm font-medium text-gray-800">
              You: +5 pts · Them: 0 pts
            </p>
            <p className="text-xs text-gray-500 mt-0.5">You gain, they lose</p>
          </div>
          <div className="rounded-lg p-3 bg-teal-50 border border-teal-200">
            <p className="text-xs font-medium text-teal-800 mb-1">
              You cooperate, they defect
            </p>
            <p className="text-sm font-medium text-gray-800">
              You: 0 pts · Them: +5 pts
            </p>
            <p className="text-xs text-gray-500 mt-0.5">You lose, they gain</p>
          </div>
        </div>
      </div>

      {/* Reminders */}
      <div className="border border-gray-200 rounded-xl p-5 mb-6">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
          Things to keep in mind
        </p>
        {[
          "You cannot communicate with your partner during the game.",
          "After each round you will see what your partner chose and your updated score.",
          "You may be paired with a different partner in future rounds — this will be noted on screen.",
        ].map((text, i) => (
          <div
            key={i}
            className="flex gap-3 items-start py-2 border-b border-gray-100 last:border-0"
          >
            <div className="w-6 h-6 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0">
              <span className="text-xs text-gray-400">→</span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed pt-0.5">
              {text}
            </p>
          </div>
        ))}
      </div>

      <Button handleClick={next} autoFocus>
        <p>Next</p>
      </Button>
    </div>
  );
}
